'use client'

import { useEffect, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { Divider, Group, Loader, Stack, Text, TextInput } from '@mantine/core'
import { IconMapPin, IconSearch } from '@tabler/icons-react'
import { reverseGeocodeToPostalCode, useGoogleMapsLoaded } from '@/components/GoogleMapsService'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { useGetPharmacyComparisonsQuery } from '@/lib/store/api/pharmacyComparisonApi'
import { useReverseGeocodeMutation } from '@/lib/store/api/geocodingApi'
import { PharmacyDetailSkuContext } from '@/lib/routes/directory_pharmacy'
import { PharmacyComparisonResult } from '@/types/interfaces/pharmacyComparison'
import { Coordinates, useCustomerCoordinates } from './useCustomerCoordinates'
import { ComparePharmaciesPanel } from './ComparePharmaciesPanel'
import { PharmacyResultsMap } from './PharmacyResultsMap'
import { PharmacyComparisonRow } from './PharmacyComparisonRow'
import {
    LABEL_BY_PRICING_GROUP,
    PharmacyQuickFilters,
    QUICK_FILTER_PRICING_GROUPS,
    QuickFilterPricingGroup,
} from './PharmacyQuickFilters'
import classes from './ComparePharmaciesDrawer.module.css'

interface ComparePharmaciesDrawerProps {
    opened: boolean
    onClose: () => void
    skuId: number
    /** Carried into each result's pharmacy-detail link so that page can offer a
     *  floating "Request medication" button back into this SKU's booking flow. */
    skuHyphenatedName: string
    productName: string
    /** Booking-attribution query param (e.g. genai) from the PDP's own URL, forwarded
     *  along so the floating CTA's re-entry into booking keeps the same attribution. */
    bookingRef?: string | null
}

// Pharmacies plotted on the Compare pharmacies map, regardless of which quick-filter
// (if any) is selected — the list below can show many more locations of a single
// chain than are useful to plot, so the map is capped to the physically nearest
// MAP_MAX_PHARMACIES among whatever the list is currently showing.
const MAP_MAX_PHARMACIES = 10

// Reduces a resolved or customer-typed zip to its first 5 digits — reverse-geocode
// results (both Nile-Core's and the client-side Google Geocoder's) can come back as a
// ZIP+4 (e.g. "02139-4891" or "02139 4891"), which the "near {zip}" header should
// always show in plain 5-digit form.
function formatFiveDigitZip(zip: string): string {
    return zip.match(/\d{5}/)?.[0] ?? zip
}

// Selects which pharmacies the list/map show for the current mode: a plain search-text
// filter while focused on or typed into either input, a single pricing group when its
// quick-filter chip is selected, or every returned pharmacy otherwise.
function selectVisiblePharmacies(
    pharmacies: PharmacyComparisonResult[],
    isSearchMode: boolean,
    needle: string,
    selectedPricingGroup: QuickFilterPricingGroup | null,
): PharmacyComparisonResult[] {
    if (isSearchMode) {
        if (!needle) return pharmacies
        return pharmacies.filter((pharmacy) => pharmacy.chain_name?.toLowerCase().includes(needle))
    }
    if (selectedPricingGroup) {
        return pharmacies.filter((pharmacy) => pharmacy.fulfillment_owner === selectedPricingGroup)
    }
    return pharmacies
}

// Uniform price for each quick-filter pricing group, for the chip labels — price is
// attached per FulfillmentOwner band, so any member of a group carries that group's
// price. A group with no nearby location has no entry here; its chip still renders,
// showing "Priced after visit" instead.
function priceCentsByPricingGroup(
    pharmacies: PharmacyComparisonResult[],
): Partial<Record<QuickFilterPricingGroup, number | null>> {
    const result: Partial<Record<QuickFilterPricingGroup, number | null>> = {}
    for (const group of QUICK_FILTER_PRICING_GROUPS) {
        const pharmacy = pharmacies.find((candidate) => candidate.fulfillment_owner === group)
        if (pharmacy) result[group] = pharmacy.total_price_cents
    }
    return result
}

export function ComparePharmaciesDrawer({
    opened,
    onClose,
    skuId,
    skuHyphenatedName,
    productName,
    bookingRef,
}: ComparePharmaciesDrawerProps) {
    const {
        coordinates: defaultCoordinates,
        isResolving: isResolvingDefaultCoordinates,
        geocodeManualAddress,
    } = useCustomerCoordinates(opened)
    // Pre-warm while the drawer is open so the results map (and the signed-out zip
    // geocode fallback) don't wait on script load only after points are ready.
    useGoogleMapsLoaded(opened)
    const [overrideCoordinates, setOverrideCoordinates] = useState<Coordinates | undefined>(undefined)
    const [locationInput, setLocationInput] = useState('')
    const [searchText, setSearchText] = useState('')
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const [isZipFocused, setIsZipFocused] = useState(false)
    const [selectedPricingGroup, setSelectedPricingGroup] = useState<QuickFilterPricingGroup | null>(null)
    // The zip the "near {zip}" header shows: whatever the customer last submitted in the
    // zip field, or — for the default geolocated/home-address coordinate, which carries
    // no zip of its own — one resolved via reverse geocoding.
    const [submittedZip, setSubmittedZip] = useState<string | undefined>(undefined)
    const [resolvedZip, setResolvedZip] = useState<string | undefined>(undefined)
    const [reverseGeocode] = useReverseGeocodeMutation()
    // Tracks the most recently submitted address so a slower geocode for an earlier one
    // can't overwrite a faster-resolving later one — only the response matching the
    // latest request is ever applied.
    const latestLocationRequestRef = useRef<string>()

    const coordinates = overrideCoordinates ?? defaultCoordinates

    useEffect(() => {
        if (overrideCoordinates || !defaultCoordinates) return

        let cancelled = false
        // Nile-Core's reverse-geocode endpoint requires an authenticated patient — a
        // signed-out visitor's own location is reverse-geocoded client-side instead, via
        // the Google Maps SDK, so this never issues an unauthenticated request that
        // would otherwise redirect a signed-out customer straight to /login.
        const resolveZip = isAuthenticatedViaMiddlewareCookie()
            ? reverseGeocode(defaultCoordinates)
                  .unwrap()
                  .then((result) => result.postal_code)
                  .catch(() => undefined)
            : reverseGeocodeToPostalCode(defaultCoordinates).catch(() => undefined)

        resolveZip.then((zip) => {
            if (!cancelled && zip) setResolvedZip(zip)
        })
        return () => {
            cancelled = true
        }
    }, [defaultCoordinates, overrideCoordinates, reverseGeocode])

    const rawDisplayZip = overrideCoordinates ? submittedZip : resolvedZip
    const displayZip = rawDisplayZip ? formatFiveDigitZip(rawDisplayZip) : undefined

    const { data, isFetching } = useGetPharmacyComparisonsQuery(
        coordinates
            ? { skuId: String(skuId), latitude: coordinates.latitude, longitude: coordinates.longitude }
            : skipToken,
    )

    const pharmacies = data?.pharmacies ?? []
    const needle = searchText.trim().toLowerCase()
    // The map and quick-filters only apply to the unfiltered, unsearched result set —
    // focusing or filling in either input hides them in favor of a plain search-filtered
    // list, reappearing only once both inputs are empty and unfocused.
    const isSearchMode = isSearchFocused || isZipFocused || searchText.trim() !== '' || locationInput.trim() !== ''

    const visiblePharmacies = selectVisiblePharmacies(pharmacies, isSearchMode, needle, selectedPricingGroup)
    const priceCentsByGroup = priceCentsByPricingGroup(pharmacies)
    // True from first render through the query actually resolving: coordinates aren't
    // available yet (skipToken keeps isFetching false during that gap) just as much as
    // the query itself being in flight once they are — both read as "no price yet",
    // not "no nearby location", so the quick-filter chips and the list below should
    // both keep showing a loading state throughout, never a premature "no price" flash.
    const isLoadingPharmacies = isFetching || (!coordinates && isResolvingDefaultCoordinates)

    const mapPoints = visiblePharmacies
        .filter((pharmacy) => pharmacy.latitude != null && pharmacy.longitude != null)
        .sort((a, b) => (a.distance_miles ?? Infinity) - (b.distance_miles ?? Infinity))
        .slice(0, MAP_MAX_PHARMACIES)
        .map((pharmacy) => ({
            fulfillment_owner: pharmacy.fulfillment_owner,
            latitude: pharmacy.latitude as number,
            longitude: pharmacy.longitude as number,
        }))

    const handleLocationSubmit = async () => {
        const address = locationInput.trim()
        if (!address) return

        latestLocationRequestRef.current = address
        const result = await geocodeManualAddress(address)
        if (result && latestLocationRequestRef.current === address) {
            setOverrideCoordinates(result)
            setSubmittedZip(address)
            // A group selected for the previous location's result set could have zero
            // nearby locations at the new one — cleared so the view falls back to the
            // default merged list instead of silently filtering to a now-empty group.
            setSelectedPricingGroup(null)
            // Clears the field back to empty so the drawer returns to the map + quick-
            // filter view (now recentered on the new location) once it's also unfocused,
            // rather than staying parked in search mode after a successful relocation.
            setLocationInput('')
        }
    }

    const skuContext: PharmacyDetailSkuContext = { skuId, skuHyphenatedName, productName, ref: bookingRef }

    return (
        <ComparePharmaciesPanel opened={opened} onClose={onClose}>
            <Group gap={8} wrap="nowrap">
                <TextInput
                    leftSection={<IconSearch size={20} color="var(--gm-foreground-fg-placeholder)" />}
                    leftSectionWidth={44}
                    placeholder="Search pharmacies"
                    value={searchText}
                    onChange={(event) => setSearchText(event.currentTarget.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    classNames={{ input: classes.pillInput, section: classes.pillIconSection }}
                    className={classes.searchInput}
                />
                <TextInput
                    leftSection={<IconMapPin size={20} color="var(--gm-foreground-fg-placeholder)" />}
                    leftSectionWidth={44}
                    placeholder={displayZip ?? 'Zip'}
                    value={locationInput}
                    onChange={(event) => setLocationInput(event.currentTarget.value)}
                    onFocus={() => setIsZipFocused(true)}
                    onBlur={() => {
                        setIsZipFocused(false)
                        handleLocationSubmit()
                    }}
                    onKeyDown={(event) => event.key === 'Enter' && handleLocationSubmit()}
                    classNames={{ input: classes.pillInput, section: classes.pillIconSection }}
                    className={classes.zipInput}
                />
            </Group>

            {/* Fills the remaining height below the search row and scrolls internally,
                so a search/zip change that shrinks or grows the result count never
                resizes the panel itself. */}
            <Stack gap={16} className={classes.resultsContainer}>
                {!isSearchMode && (
                    <PharmacyQuickFilters
                        priceCentsByGroup={priceCentsByGroup}
                        isLoading={isLoadingPharmacies}
                        selected={selectedPricingGroup}
                        onSelect={setSelectedPricingGroup}
                    />
                )}

                {/* Omitted in search mode, where the map and quick-filters give way to a
                    plain filtered list. */}
                {!isSearchMode && mapPoints.length > 0 && (
                    <PharmacyResultsMap points={mapPoints} origin={coordinates} />
                )}

                {!isSearchMode && displayZip && (
                    <Text fz={14} fw="var(--gm-weight-text-bold, 600)" c="var(--gm-foreground-fg-primary)">
                        {selectedPricingGroup ? LABEL_BY_PRICING_GROUP[selectedPricingGroup] : 'Pharmacies'} near{' '}
                        {displayZip}
                    </Text>
                )}

                {/* The zip field above remains available if location resolution gives
                    up (isResolvingDefaultCoordinates goes false) without a result, so
                    the list underneath is reachable either way. */}
                {isLoadingPharmacies ? (
                    <Group justify="center" p={24}>
                        <Loader size="sm" />
                    </Group>
                ) : (
                    <Stack gap={0} className={classes.list}>
                        {visiblePharmacies.map((pharmacy, index) => (
                            <div key={pharmacy.friendly_id ?? pharmacy.ncpdp_provider_id}>
                                {index > 0 && <Divider my={16} />}
                                <PharmacyComparisonRow pharmacy={pharmacy} skuContext={skuContext} />
                            </div>
                        ))}
                    </Stack>
                )}
            </Stack>
        </ComparePharmaciesPanel>
    )
}
